/**
 * GameContext - State management for Easy Daily game flow
 * Manages the state machine: BOOT → LOADING → ORPHAN_CHECK → SPIN → SPIN_RESULT → PRESENTATION → RECAP
 */

import { createContext, useContext, useReducer, ReactNode, useCallback } from 'react';
import type { Board, Sprint, SquadMember, Issue, Blocker, ChouquetteDebt } from '../types/jira';
import type { Maze } from '../types/pacman';

// Game phases
export type GamePhase =
  | 'CARTRIDGE_SELECT'
  | 'BOOT'
  | 'LOADING'
  | 'ORPHAN_CHECK'
  | 'GAME_MODE_SELECT'
  | 'SPIN'
  | 'SPIN_RESULT'
  | 'PACMAN_MAZE'
  | 'PACMAN_KANBAN'
  | 'PRESENTATION'
  | 'RECAP';

// Game mode selection
export type GameMode = 'SLOT' | 'PACMAN';

// Display modes for issues
export type DisplayMode = 'list' | 'kanban';

// Game state
export interface GameState {
  phase: GamePhase;
  selectedBoard: Board | null;
  selectedSprint: Sprint | null;
  members: SquadMember[];
  remainingMembers: SquadMember[];
  lateMembers: SquadMember[];
  absentMembers: SquadMember[];
  currentMember: SquadMember | null;
  orphanIssues: Issue[];
  blockers: Blocker[];
  chouquetteMode: boolean;
  chouquetteDebts: ChouquetteDebt[];
  dailyStartTime: Date | null;
  issuesWithoutEstimate: Issue[];
  error: string | null;
  isLoading: boolean;
  displayMode: DisplayMode;
  soundEnabled: boolean;
  // Pac-Man mode
  gameMode: GameMode | null;
  pacmanCaptureOrder: string[];
  pacmanMaze: Maze | null;
}

// Actions
type GameAction =
  | { type: 'SET_PHASE'; payload: GamePhase }
  | { type: 'SELECT_BOARD'; payload: Board }
  | { type: 'SELECT_SPRINT'; payload: Sprint }
  | { type: 'SET_MEMBERS'; payload: SquadMember[] }
  | { type: 'SET_ORPHAN_ISSUES'; payload: Issue[] }
  | { type: 'SET_UNESTIMATED_ISSUES'; payload: Issue[] }
  | { type: 'SELECT_MEMBER'; payload: SquadMember }
  | { type: 'MARK_ABSENT'; payload: SquadMember }
  | { type: 'MARK_LATE'; payload: SquadMember }
  | { type: 'COMPLETE_TURN' }
  | { type: 'ADD_BLOCKER'; payload: Blocker }
  | { type: 'TOGGLE_CHOUQUETTE_MODE' }
  | { type: 'ADD_CHOUQUETTE_DEBT'; payload: ChouquetteDebt }
  | { type: 'START_DAILY' }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_DISPLAY_MODE'; payload: DisplayMode }
  | { type: 'RESET' }
  // Pac-Man mode actions
  | { type: 'SET_GAME_MODE'; payload: GameMode }
  | { type: 'ADD_PACMAN_CAPTURE'; payload: string }
  | { type: 'RESET_PACMAN_CAPTURES' }
  | { type: 'COMPLETE_PACMAN_TURN' }
  | { type: 'SET_PACMAN_MAZE'; payload: Maze }
  | { type: 'CLEAR_PACMAN_MAZE' }
  | { type: 'TOGGLE_SOUND' };

// Determine initial phase based on saved provider
const getInitialPhase = (): GamePhase => {
  const savedProvider = localStorage.getItem('selectedProvider');
  return savedProvider ? 'BOOT' : 'CARTRIDGE_SELECT';
};

// Initial state
const initialState: GameState = {
  phase: getInitialPhase(),
  selectedBoard: null,
  selectedSprint: null,
  members: [],
  remainingMembers: [],
  lateMembers: [],
  absentMembers: [],
  currentMember: null,
  orphanIssues: [],
  blockers: [],
  chouquetteMode: false, // Easter egg - must be activated each session
  chouquetteDebts: [],
  dailyStartTime: null,
  issuesWithoutEstimate: [],
  error: null,
  isLoading: false,
  displayMode: (localStorage.getItem('displayMode') as DisplayMode) || 'kanban',
  soundEnabled: true, // ON par défaut, pas de persistance
  // Pac-Man mode
  gameMode: (localStorage.getItem('gameMode') as GameMode) || null,
  pacmanCaptureOrder: [],
  pacmanMaze: null,
};

// Reducer
function gameReducer(state: GameState, action: GameAction): GameState {
  switch (action.type) {
    case 'SET_PHASE':
      return { ...state, phase: action.payload };

    case 'SELECT_BOARD':
      return { ...state, selectedBoard: action.payload };

    case 'SELECT_SPRINT':
      return { ...state, selectedSprint: action.payload };

    case 'SET_MEMBERS':
      return {
        ...state,
        members: action.payload,
        remainingMembers: [...action.payload],
      };

    case 'SET_ORPHAN_ISSUES':
      return { ...state, orphanIssues: action.payload };

    case 'SET_UNESTIMATED_ISSUES':
      return { ...state, issuesWithoutEstimate: action.payload };

    case 'SELECT_MEMBER':
      return {
        ...state,
        currentMember: action.payload,
        remainingMembers: state.remainingMembers.filter(
          (m) => m.accountId !== action.payload.accountId
        ),
      };

    case 'MARK_ABSENT':
      return {
        ...state,
        absentMembers: [...state.absentMembers, action.payload],
        remainingMembers: state.remainingMembers.filter(
          (m) => m.accountId !== action.payload.accountId
        ),
        currentMember: null,
      };

    case 'MARK_LATE':
      return {
        ...state,
        lateMembers: [...state.lateMembers, action.payload],
        remainingMembers: state.remainingMembers.filter(
          (m) => m.accountId !== action.payload.accountId
        ),
        currentMember: null,
      };

    case 'COMPLETE_TURN':
      // Check if there are more remaining members
      if (state.remainingMembers.length > 0) {
        return { ...state, phase: 'SPIN', currentMember: null };
      }
      // Check if there are late members to process
      if (state.lateMembers.length > 0) {
        const nextLate = state.lateMembers[0];
        return {
          ...state,
          currentMember: nextLate,
          lateMembers: state.lateMembers.slice(1),
          phase: 'PRESENTATION',
        };
      }
      // No more members - go to recap
      return { ...state, phase: 'RECAP', currentMember: null };

    case 'ADD_BLOCKER':
      return { ...state, blockers: [...state.blockers, action.payload] };

    case 'TOGGLE_CHOUQUETTE_MODE':
      return { ...state, chouquetteMode: !state.chouquetteMode };

    case 'ADD_CHOUQUETTE_DEBT':
      return { ...state, chouquetteDebts: [...state.chouquetteDebts, action.payload] };

    case 'START_DAILY':
      return { ...state, dailyStartTime: new Date() };

    case 'SET_ERROR':
      return { ...state, error: action.payload };

    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_DISPLAY_MODE': {
      localStorage.setItem('displayMode', action.payload);
      return { ...state, displayMode: action.payload };
    }

    case 'RESET':
      return {
        ...initialState,
        displayMode: state.displayMode,
        gameMode: state.gameMode, // Preserve game mode preference
        soundEnabled: state.soundEnabled, // Preserve sound preference for session
      };

    case 'SET_GAME_MODE': {
      localStorage.setItem('gameMode', action.payload);
      return { ...state, gameMode: action.payload };
    }

    case 'ADD_PACMAN_CAPTURE':
      return {
        ...state,
        pacmanCaptureOrder: [...state.pacmanCaptureOrder, action.payload],
      };

    case 'RESET_PACMAN_CAPTURES':
      return { ...state, pacmanCaptureOrder: [] };

    case 'COMPLETE_PACMAN_TURN': {
      // Similar to COMPLETE_TURN but for Pac-Man mode
      if (state.remainingMembers.length > 0) {
        return { ...state, phase: 'PACMAN_MAZE', currentMember: null };
      }
      if (state.lateMembers.length > 0) {
        const nextLate = state.lateMembers[0];
        return {
          ...state,
          currentMember: nextLate,
          lateMembers: state.lateMembers.slice(1),
          phase: 'PACMAN_KANBAN',
        };
      }
      return { ...state, phase: 'RECAP', currentMember: null };
    }

    case 'SET_PACMAN_MAZE':
      return { ...state, pacmanMaze: action.payload };

    case 'CLEAR_PACMAN_MAZE':
      return { ...state, pacmanMaze: null, pacmanCaptureOrder: [] };

    case 'TOGGLE_SOUND':
      return { ...state, soundEnabled: !state.soundEnabled };

    default:
      return state;
  }
}

// Context types
interface GameContextType {
  state: GameState;
  dispatch: React.Dispatch<GameAction>;
  actions: {
    setPhase: (phase: GamePhase) => void;
    selectBoard: (board: Board) => void;
    selectSprint: (sprint: Sprint) => void;
    setMembers: (members: SquadMember[]) => void;
    setOrphanIssues: (issues: Issue[]) => void;
    setUnestimatedIssues: (issues: Issue[]) => void;
    selectMember: (member: SquadMember) => void;
    markAbsent: (member: SquadMember) => void;
    markLate: (member: SquadMember) => void;
    completeTurn: () => void;
    addBlocker: (blocker: Blocker) => void;
    toggleChouquetteMode: () => void;
    addChouquetteDebt: (debt: ChouquetteDebt) => void;
    startDaily: () => void;
    setError: (error: string | null) => void;
    setLoading: (loading: boolean) => void;
    setDisplayMode: (mode: DisplayMode) => void;
    reset: () => void;
    // Pac-Man mode
    setGameMode: (mode: GameMode) => void;
    addPacmanCapture: (memberId: string) => void;
    resetPacmanCaptures: () => void;
    completePacmanTurn: () => void;
    setPacmanMaze: (maze: Maze) => void;
    clearPacmanMaze: () => void;
    toggleSound: () => void;
  };
}

// Context
const GameContext = createContext<GameContextType | null>(null);

// Provider component
export function GameProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(gameReducer, initialState);

  const actions = {
    setPhase: useCallback((phase: GamePhase) => dispatch({ type: 'SET_PHASE', payload: phase }), []),
    selectBoard: useCallback((board: Board) => dispatch({ type: 'SELECT_BOARD', payload: board }), []),
    selectSprint: useCallback((sprint: Sprint) => dispatch({ type: 'SELECT_SPRINT', payload: sprint }), []),
    setMembers: useCallback((members: SquadMember[]) => dispatch({ type: 'SET_MEMBERS', payload: members }), []),
    setOrphanIssues: useCallback((issues: Issue[]) => dispatch({ type: 'SET_ORPHAN_ISSUES', payload: issues }), []),
    setUnestimatedIssues: useCallback((issues: Issue[]) => dispatch({ type: 'SET_UNESTIMATED_ISSUES', payload: issues }), []),
    selectMember: useCallback((member: SquadMember) => dispatch({ type: 'SELECT_MEMBER', payload: member }), []),
    markAbsent: useCallback((member: SquadMember) => dispatch({ type: 'MARK_ABSENT', payload: member }), []),
    markLate: useCallback((member: SquadMember) => dispatch({ type: 'MARK_LATE', payload: member }), []),
    completeTurn: useCallback(() => dispatch({ type: 'COMPLETE_TURN' }), []),
    addBlocker: useCallback((blocker: Blocker) => dispatch({ type: 'ADD_BLOCKER', payload: blocker }), []),
    toggleChouquetteMode: useCallback(() => dispatch({ type: 'TOGGLE_CHOUQUETTE_MODE' }), []),
    addChouquetteDebt: useCallback((debt: ChouquetteDebt) => dispatch({ type: 'ADD_CHOUQUETTE_DEBT', payload: debt }), []),
    startDaily: useCallback(() => dispatch({ type: 'START_DAILY' }), []),
    setError: useCallback((error: string | null) => dispatch({ type: 'SET_ERROR', payload: error }), []),
    setLoading: useCallback((loading: boolean) => dispatch({ type: 'SET_LOADING', payload: loading }), []),
    setDisplayMode: useCallback((mode: DisplayMode) => dispatch({ type: 'SET_DISPLAY_MODE', payload: mode }), []),
    reset: useCallback(() => dispatch({ type: 'RESET' }), []),
    // Pac-Man mode
    setGameMode: useCallback((mode: GameMode) => dispatch({ type: 'SET_GAME_MODE', payload: mode }), []),
    addPacmanCapture: useCallback((memberId: string) => dispatch({ type: 'ADD_PACMAN_CAPTURE', payload: memberId }), []),
    resetPacmanCaptures: useCallback(() => dispatch({ type: 'RESET_PACMAN_CAPTURES' }), []),
    completePacmanTurn: useCallback(() => dispatch({ type: 'COMPLETE_PACMAN_TURN' }), []),
    setPacmanMaze: useCallback((maze: Maze) => dispatch({ type: 'SET_PACMAN_MAZE', payload: maze }), []),
    clearPacmanMaze: useCallback(() => dispatch({ type: 'CLEAR_PACMAN_MAZE' }), []),
    toggleSound: useCallback(() => dispatch({ type: 'TOGGLE_SOUND' }), []),
  };

  return (
    <GameContext.Provider value={{ state, dispatch, actions }}>
      {children}
    </GameContext.Provider>
  );
}

// Hook for using the context
export function useGame() {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
}
